import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, LogOut, Inbox as InboxIcon, Mail, Phone, MapPin, Loader2, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "fts_admin_token";

const inputCls =
  "w-full bg-transparent border-b border-[#D1CFC7] rounded-none px-0 py-3 focus:outline-none focus:border-[#2D4A3E] text-[#1E352F] placeholder-[#9A9A92] transition-colors";

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <motion.form
        data-testid="inbox-login-form"
        onSubmit={submit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md rounded-sm border border-[#D1CFC7] bg-[#FFFFFF] p-8"
      >
        <div className="flex items-center gap-2 text-[#2D4A3E]">
          <Lock className="h-5 w-5" />
          <span className="text-xs tracking-[0.2em] uppercase font-semibold">Private</span>
        </div>
        <h1 className="font-serif-display text-4xl text-[#1E352F] mt-2">Quote Inbox</h1>
        <p className="text-sm text-[#4A5D54] mt-1">Sign in to view your quote requests.</p>
        <div className="mt-6 space-y-5">
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-[#4A5D54]">Email</label>
            <input data-testid="inbox-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@email.com" />
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-[#4A5D54]">Password</label>
            <input data-testid="inbox-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" />
          </div>
        </div>
        <button
          type="submit"
          data-testid="inbox-login-button"
          disabled={loading}
          className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#2D4A3E] text-[#F8F7F4] px-6 py-3 rounded-sm font-medium hover:bg-[#1E352F] transition-colors disabled:opacity-60"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign In
        </button>
      </motion.form>
    </div>
  );
}

function QuoteCard({ q, deleted, onDelete, onRestore }) {
  return (
    <motion.div
      data-testid={deleted ? "inbox-deleted-card" : "inbox-quote-card"}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-sm border border-[#D1CFC7] bg-[#FFFFFF] p-6"
    >
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <p className="font-serif-display text-2xl text-[#1E352F]">{q.name}</p>
          {q.project_type && (
            <span className="inline-block mt-1 text-xs tracking-wide px-2.5 py-1 rounded-sm bg-[#EAE7E0] text-[#2D4A3E] border border-[#D1CFC7]">
              {q.project_type}
            </span>
          )}
        </div>
        <span className="text-sm text-[#8C8C84]">
          {new Date(q.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
        </span>
      </div>

      <p className="text-[#4A5D54] mt-3 leading-relaxed whitespace-pre-wrap">{q.message}</p>

      <div className="mt-4 pt-4 border-t border-[#EAE7E0] flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <a href={`mailto:${q.email}`} className="flex items-center gap-2 text-[#2D4A3E] hover:underline">
          <Mail className="h-4 w-4 text-[#A95C42]" /> {q.email}
        </a>
        {q.phone && (
          <a href={`tel:${q.phone}`} className="flex items-center gap-2 text-[#2D4A3E] hover:underline">
            <Phone className="h-4 w-4 text-[#A95C42]" /> {q.phone}
          </a>
        )}
        {q.location && (
          <span className="flex items-center gap-2 text-[#4A5D54]">
            <MapPin className="h-4 w-4 text-[#A95C42]" /> {q.location}
          </span>
        )}
        {deleted ? (
          <button
            data-testid="inbox-restore-button"
            onClick={() => onRestore(q.id)}
            className="ml-auto inline-flex items-center gap-1.5 text-sm text-[#2D4A3E] hover:text-[#1E352F] transition-colors"
            title="Restore this request"
          >
            <RotateCcw className="h-4 w-4" /> Restore
          </button>
        ) : (
          <button
            data-testid="inbox-delete-button"
            onClick={() => onDelete(q.id)}
            className="ml-auto inline-flex items-center gap-1.5 text-sm text-[#A04A38] hover:text-[#7E3526] transition-colors"
            title="Delete this request"
          >
            <Trash2 className="h-4 w-4" /> Delete
          </button>
        )}
      </div>
    </motion.div>
  );
}

function QuotesView({ onLogout }) {
  const [tab, setTab] = useState("inbox");
  const [quotes, setQuotes] = useState(null);
  const [deleted, setDeleted] = useState(null);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

  const handleErr = (err, msg) => {
    if (err.response?.status === 401) onLogout();
    else toast.error(msg);
  };

  const loadInbox = async () => {
    try {
      const { data } = await axios.get(`${API}/quotes`, { headers: authHeader() });
      setQuotes(data);
    } catch (err) {
      handleErr(err, "Could not load quotes.");
    }
  };

  const loadDeleted = async () => {
    try {
      const { data } = await axios.get(`${API}/quotes/deleted`, { headers: authHeader() });
      setDeleted(data);
    } catch (err) {
      handleErr(err, "Could not load deleted requests.");
    }
  };

  const removeQuote = async (id) => {
    if (!window.confirm("Move this request to Deleted? You can restore it within 30 days.")) return;
    const prev = quotes;
    setQuotes((q) => q.filter((item) => item.id !== id));
    try {
      await axios.delete(`${API}/quotes/${id}`, { headers: authHeader() });
      toast.success("Moved to Deleted.");
      setDeleted(null);
    } catch (err) {
      setQuotes(prev);
      handleErr(err, "Could not delete. Try again.");
    }
  };

  const restoreQuote = async (id) => {
    const prev = deleted;
    setDeleted((q) => q.filter((item) => item.id !== id));
    try {
      await axios.post(`${API}/quotes/${id}/restore`, {}, { headers: authHeader() });
      toast.success("Request restored.");
      setQuotes(null);
    } catch (err) {
      setDeleted(prev);
      handleErr(err, "Could not restore. Try again.");
    }
  };

  useEffect(() => {
    if (tab === "inbox" && quotes === null) loadInbox();
    if (tab === "deleted" && deleted === null) loadDeleted();
  }, [tab, quotes, deleted]);

  const active = tab === "inbox" ? quotes : deleted;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#A95C42]">
            <InboxIcon className="h-5 w-5" />
            <span className="text-xs tracking-[0.2em] uppercase font-semibold">Quote Inbox</span>
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1E352F] mt-1">
            {tab === "inbox" ? "Your Requests" : "Deleted"}{" "}
            {active ? `(${active.length})` : ""}
          </h1>
        </div>
        <button
          data-testid="inbox-logout-button"
          onClick={onLogout}
          className="inline-flex items-center gap-2 border border-[#2D4A3E] text-[#2D4A3E] px-4 py-2 rounded-sm font-medium hover:bg-[#EAE7E0] transition-colors"
        >
          <LogOut className="h-4 w-4" /> Sign Out
        </button>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex gap-2 border-b border-[#D1CFC7]">
        <button
          data-testid="inbox-tab-inbox"
          onClick={() => setTab("inbox")}
          className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === "inbox" ? "border-[#2D4A3E] text-[#1E352F]" : "border-transparent text-[#8C8C84] hover:text-[#2D4A3E]"}`}
        >
          Inbox
        </button>
        <button
          data-testid="inbox-tab-deleted"
          onClick={() => setTab("deleted")}
          className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors inline-flex items-center gap-1.5 ${tab === "deleted" ? "border-[#2D4A3E] text-[#1E352F]" : "border-transparent text-[#8C8C84] hover:text-[#2D4A3E]"}`}
        >
          <Trash2 className="h-4 w-4" /> Deleted
        </button>
      </div>

      {tab === "deleted" && (
        <p className="mt-4 text-sm text-[#8C8C84]">
          Deleted requests are kept here for 30 days, then removed permanently.
        </p>
      )}

      {active === null ? (
        <div className="mt-12 flex justify-center text-[#4A5D54]">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : active.length === 0 ? (
        <div className="mt-8 rounded-sm border border-dashed border-[#D1CFC7] p-12 text-center text-[#4A5D54]">
          {tab === "inbox"
            ? "No quote requests yet. They'll show up here the moment someone submits the form."
            : "Nothing in Deleted."}
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          {active.map((q) => (
            <QuoteCard
              key={q.id}
              q={q}
              deleted={tab === "deleted"}
              onDelete={removeQuote}
              onRestore={restoreQuote}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Inbox() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  return (
    <main data-testid="inbox-page" className="pt-20 min-h-screen bg-[#F8F7F4]">
      {authed ? (
        <QuotesView onLogout={logout} />
      ) : (
        <LoginView onLogin={() => setAuthed(true)} />
      )}
    </main>
  );
}
